import CustomHttpHeaders from './custom-http-headers';
import { mapNetworkError, mapServerError } from './functions';

const headersForJson = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

function _isJSON(str: string) {
  try {
    return JSON.parse(str) && !!str;
  } catch (e) {
    return false;
  }
}

function _parseJSON(str: string) {
  return _isJSON(str) ? JSON.parse(str) : str;
}

const headersWithAuthentication = (token?: string) => ({
  ...headersForJson,
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const checkResponse = async (response: Response) => {
  return response.text().then((text: string) => {
    if (response.ok) {
      if (response.status === 204) {
        return {
          status: response.status,
          statusText: response.statusText,
        };
      } else {
        return _parseJSON(text);
      }
    } else {
      const error = !_isJSON(text) ? text : _parseJSON(text);
      throw {
        error,
        status: response.status,
      };
    }
  });
};

const handleFetchError = (error: any) => {
  if (!error.hasOwnProperty('status')) {
    throw mapNetworkError(error);
  }

  if (error.status >= 500) {
    throw mapServerError(error);
  }

  throw error;
};

type ClientSendParams = {
  method: string;
  url: string;
  data?: any | undefined;
  stringifyBody?: boolean;
  additionalHeaders?: { [key: string]: string };
};

const clientSend = (token?: string) => async ({
  method,
  url,
  data = undefined,
  stringifyBody = false,
  additionalHeaders = {},
}: ClientSendParams) => {
  const body = stringifyBody ? JSON.stringify(data) : data;
  const headers = {
    ...headersWithAuthentication(token),
    ...CustomHttpHeaders.get(),
    ...additionalHeaders,
  };

  return fetch(url, {
    body,
    headers,
    method,
    cache: 'default',
  })
    .then(checkResponse)
    .catch(handleFetchError);
};

export const createFetchAndCheckResponse = async (url: string, params: any) => {
  return fetch(url, params).then(checkResponse);
};

export const parseFormData = (data: { [key: string]: any }) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (Array.isArray(data[key])) {
      data[key].forEach((e: string | Blob) => formData.append(key, e));
    } else {
      formData.append(key, data[key]);
    }
  });

  return formData;
};

type ClientSendFormDataParams = {
  method: string;
  url: string;
  data: any | undefined;
  additionalHeaders: { [key: string]: string };
};

const clientSendFormData = (token?: string) => async ({
  method,
  url,
  data,
  additionalHeaders = {},
}: ClientSendFormDataParams) => {
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...additionalHeaders,
  };

  return fetch(url, {
    method,
    headers,
    body: parseFormData(data),
  })
    .then(checkResponse)
    .catch(handleFetchError);
};

export const buildApi = (token?: string) => {
  const sendWithToken = clientSend(token);
  const sendFormData = clientSendFormData(token);

  return {
    get: (url: string) => sendWithToken({ method: 'GET', url }),
    del: (url: string) => sendWithToken({ method: 'DELETE', url }),
    post: (url: string, data: any, stringifyBody: boolean, additionalHeaders = {}) =>
      sendWithToken({ method: 'POST', url, data, stringifyBody, additionalHeaders }),
    put: (url: string, data: any, stringifyBody: boolean, additionalHeaders = {}) =>
      sendWithToken({ method: 'PUT', url, data, stringifyBody, additionalHeaders }),
    patch: (url: string, data: any, stringifyBody: boolean, additionalHeaders = {}) =>
      sendWithToken({ method: 'PATCH', url, data, stringifyBody, additionalHeaders }),
    postFormData: (url: string, data: any, additionalHeaders = {}) =>
      sendFormData({ method: 'POST', url, data, additionalHeaders }),
  };
};
